import { Button, Group, Box, useMantineTheme, Text } from "@mantine/core";
import { memo, useContext, useEffect } from "react";
import { useModals } from "@mantine/modals";
import { AppContext } from "src/api/AppContext";
import {
  useClockInMutation,
  useClockOutMutation,
  useGetEmployeesStationNameManyQuery,
} from "src/generated/graphql";
import { errorNotification } from "src/utils/alerts";
import { useForm, yupResolver } from "@mantine/form";
import Select from "src/components/Select";
import { object, string } from "yup";

type IFormProps = {
  station: string;
};

type IClock = {
  stationsEnabled: boolean;
  isClockedIn: boolean;
};

const Clock = ({ stationsEnabled, isClockedIn }: IClock) => {
  const modals = useModals();
  const theme = useMantineTheme();
  const { currentLocation, currentEmployee, getEmployee } =
    useContext(AppContext);

  const { data: stations } = useGetEmployeesStationNameManyQuery({
    variables: {
      employeeId: currentEmployee?.id || "",
      locationId: currentLocation?.id || "",
    },
    skip: !currentEmployee || !currentLocation,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const { station } = currentEmployee?.activeSession || {};
    if (station) {
      form.setFieldValue("station", station.id);
    }
  }, [currentEmployee?.activeSession]);

  const form = useForm<IFormProps>({
    initialValues: {
      station: "",
    },
    validate: yupResolver(
      object().shape({
        ...(!isClockedIn &&
          stationsEnabled && {
            station: string().required("Station is required"),
          }),
      })
    ),
  });

  const [clockIn, { loading: loadingCheckIn }] = useClockInMutation({
    onCompleted: () => {
      getEmployee();
      handleClose();
    },
  });

  const [clockOut, { loading: loadingCheckOut }] = useClockOutMutation({
    onCompleted: () => {
      getEmployee();
      handleClose();
    },
  });

  const handleClose = () => {
    modals.closeAll();
  };

  const handleSubmit = async ({ station }: IFormProps) => {
    let activeLoc = currentEmployee?.activeSession?.location;
    if (activeLoc && activeLoc.id !== currentLocation?.id) {
      return errorNotification({
        message: "Clock in Location Not Matched with Current Location",
      });
    }
    currentEmployee?.clockedIn
      ? clockOut({
          variables: {
            data: {
              endedAt: new Date().toJSON(),
              location: { id: currentLocation!.id },
            },
          },
        })
      : clockIn({
          variables: {
            data: {
              startedAt: new Date().toJSON(),
              location: { id: currentLocation!.id },
              ...(station && { station: { id: station } }),
            },
          },
        });
  };

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        handleSubmit(values);
      })}
    >
      <Box sx={{ maxWidth: 360 }} mx="auto">
        <Text sx={{ fontWeight: 500 }} mb={"md"} align="center">
          Current Location: {currentLocation?.name}
        </Text>

        {stationsEnabled &&
          (isClockedIn ? !!form.getInputProps("station").value : true) && (
            <Select
              data={
                stations?.employeeStationMany?.map((val) => ({
                  value: val.id,
                  label: val.name,
                })) || []
              }
              label="Select Station"
              disabled={!!isClockedIn}
              {...form.getInputProps("station")}
            />
          )}

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={handleClose}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={loadingCheckOut || loadingCheckIn}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={loadingCheckOut || loadingCheckIn}
          >
            Confirm
          </Button>
        </Group>
      </Box>
    </form>
  );
};

export default memo(Clock);
